import { Button } from 'shared/ui/Button';
import { BaseCheckbox } from 'shared/ui/Checkbox';
import { SelectIndependentQuery, type ListQuery } from 'shared/ui/Select';
import { AlertModal, SideModal, SideModalBody, SideModalFooter, useModalsActions } from 'shared/ui/SideModal';
import type { ResourceSuccessApiResponse } from 'shared/lib/api';
import { useGetData } from 'shared/lib/api';
import { DROPDOWN_ITEMS_LIMIT } from 'shared/constants';
import type { IntegrationModel } from 'entities/integration';
import { integrationsRoutes } from 'entities/integration';
import { SortDirection } from 'shared/ui/Sortable';
import { getDataAutoTestAttributes } from 'shared/lib/data-auto-test';
import { useSetupIntegration } from 'entities/campaign-integrations';
import type { MappableListItem } from 'shared/lib/mappers';

import { getIntegrationFieldsFromArray, IntegrationFieldsList } from './IntegrationFieldsList';
import { ManagerIntegrationSort } from './filter';
import { convertMetaToData, useIntegrationFields } from './useIntegrationFields';

interface AddIntegrationModalProps {
  campaignId: number;
}

export function AddIntegrationModal({ campaignId }: AddIntegrationModalProps) {
  const { forceClose } = useModalsActions();
  const setupIntegrationQuery = useSetupIntegration(campaignId);
  const getIntegrationsQuery = useGetData<ResourceSuccessApiResponse<IntegrationModel[]>>({
    url: integrationsRoutes.internal.getIntegrations,
    defaultQueryParams: {
      [ManagerIntegrationSort.label]: SortDirection.asc,
      limit: DROPDOWN_ITEMS_LIMIT.toString(),
    },
  });

  const {
    integration,
    isDirty,
    isDisabled,
    setIntegrationId,
    fields,
    integrationId,
    doNotActivateOnAdd,
    validate,
    onChange,
    append,
    remove,
    errors,
    setDoNotActivateOnAdd,
    hasMeta,
  } = useIntegrationFields({
    integrationData: getIntegrationsQuery.data?.data,
  });

  const onIntegrationSelectHandler = (id: string) => {
    setIntegrationId(id);
  };

  const submit = () => {
    const { hasError } = validate();

    if (!hasError) {
      setupIntegrationQuery.mutate(
        {
          payload: {
            campaignId,
            integrationId: Number(integrationId),
            fields: getIntegrationFieldsFromArray(hasMeta ? convertMetaToData(fields) : fields),
            isActive: !doNotActivateOnAdd,
          },
        },
        { onSuccess: forceClose }
      );
    }
  };

  return (
    <SideModal autoTestAttributesName="add-integration-modal">
      <SideModalBody title="Add integration">
        <form
          className="pb-8"
          onSubmit={submit}
        >
          <AlertModal
            autoTestAttributesName="add-integration-exit-modal"
            showCondition={isDirty}
          />
          <div className="mb-4">
            <SelectIndependentQuery
              title="Saved templates"
              selectedId={integrationId}
              onChange={onIntegrationSelectHandler}
              getFieldValueToShow={(item) => (item as Record<string, string>).label}
              listQuery={getIntegrationsQuery as ListQuery<MappableListItem>}
              isSmall={false}
              isCancel={false}
              isCustomScrollbar
              isSearch
              noOptional
              {...getDataAutoTestAttributes(['select-integrations-templates'])}
            />
          </div>
          {integration && (
            <IntegrationFieldsList
              key={integration.id}
              fields={fields}
              errors={errors}
              onChange={onChange}
              append={append}
              remove={remove}
            />
          )}
        </form>
      </SideModalBody>
      <SideModalFooter>
        <Button
          onClick={submit}
          className="mb-4 w-full"
          disabled={isDisabled}
          loading={setupIntegrationQuery.isLoading}
          dataTestId="AddIntegrationFormBtn"
          {...getDataAutoTestAttributes(['button-add-integration'])}
        >
          Add integration
        </Button>
        <div className="flex justify-center">
          <BaseCheckbox
            isDisabled={isDisabled}
            onChange={(e) => setDoNotActivateOnAdd(e.target.checked)}
            {...getDataAutoTestAttributes(['checkbox-do-not-activate'])}
          >
            <span className="text-sm">Do not activate after adding</span>
          </BaseCheckbox>
        </div>
      </SideModalFooter>
    </SideModal>
  );
}
